import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { usePlans, PLAN_STATUSES } from "../../plans/PlansContext";
import { useInventory } from "../../inventory/InventoryContext";
import {
  budgetsOf,
  lineShare,
  planCurrencies,
  planLines,
  planTotals,
  splitBudget,
  formatAmount,
  formatCount,
} from "../../plans/planMath";
import { exportPlanXlsx, exportPlanPdf } from "../../plans/planExport";
import { formatMoney, formatReach, joinList } from "../../inventory/parse";
import { formatDateTime, timeAgo } from "../../inventory/time";
import Icon from "../../components/ui/Icon";
import Modal from "../../components/ui/Modal";
import Popover from "../../components/ui/Popover";
import "../inventory/InventoryPage.css";
import "./Plans.css";

const num = (v) => (v === "" || v == null ? "" : v);

export default function PlanPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { plans, updatePlan, updateLine, removeLine, setItems, deletePlan, duplicatePlan } = usePlans();
  const { channels } = useInventory();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [savingPdf, setSavingPdf] = useState(false);

  const plan = plans.find((p) => p.id === id);
  const channelsById = useMemo(() => new Map(channels.map((c) => [c.id, c])), [channels]);
  const lines = useMemo(() => (plan ? planLines(plan, channelsById) : []), [plan, channelsById]);
  const totals = useMemo(() => (plan ? planTotals(plan, lines) : null), [plan, lines]);

  if (!plan) {
    return (
      <div className="inv-card">
        <div className="inv-empty">
          <h2>Plan not found</h2>
          <p>It may have been deleted, or it was made in another browser.</p>
          <div className="inv-empty-actions">
            <Link to="/plans" className="btn">
              Back to media plans
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const set = (key) => (e) => updatePlan(plan.id, { [key]: e.target.value });

  // One planned budget per currency the plan's channels are priced in
  const budgets = budgetsOf(plan);
  const currencies = planCurrencies(plan, lines);
  const hasBudget = totals.byCurrency.some((c) => c.planned > 0);
  const setBudget = (currency, value) => {
    const next = { ...budgets };
    if (value === "") delete next[currency];
    else next[currency] = Number(value);
    updatePlan(plan.id, { budgets: next });
  };
  const byCurrency = (fn) => totals.byCurrency.filter((c) => c.channels > 0).map(fn);

  return (
    <div className="plan">
      <Link to="/plans" className="plan-back no-print">
        <Icon name="chevron" size={14} className="rot-90" /> Media plans
      </Link>

      <div className="plan-head">
        <div className="plan-title">
          <input
            className="plan-name"
            value={plan.name}
            onChange={set("name")}
            aria-label="Plan name"
            placeholder="Plan name"
          />
          <div className="plan-sub">
            <select
              className={`status status-${plan.status.toLowerCase()}`}
              value={plan.status}
              onChange={set("status")}
              aria-label="Status"
            >
              {PLAN_STATUSES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
            <span title={formatDateTime(plan.updatedAt)}>
              Updated {timeAgo(plan.updatedAt)} by {plan.updatedBy}
            </span>
          </div>
        </div>

        <div className="inv-actions no-print">
          <Link to="/inventory" className="btn">
            <Icon name="plus" /> Add channels
          </Link>
          <button
            type="button"
            className="btn"
            disabled={!lines.length}
            onClick={() => exportPlanXlsx(plan, lines, totals)}
          >
            <Icon name="download" /> Excel
          </button>
          <button type="button" className="btn" onClick={() => window.print()}>
            <Icon name="file" /> Print
          </button>
          <button
            type="button"
            className="btn"
            disabled={!lines.length || savingPdf}
            onClick={async () => {
              setSavingPdf(true);
              try {
                await exportPlanPdf(plan, lines, totals);
              } catch {
                window.alert("Could not create the PDF. Please try again.");
              } finally {
                setSavingPdf(false);
              }
            }}
          >
            <Icon name="download" /> {savingPdf ? "Saving…" : "Save as PDF"}
          </button>
          <Popover align="right" triggerClass="btn btn-square" label="More actions" trigger={<Icon name="more" />}>
            {(close) => (
              <>
                <button
                  type="button"
                  className="menu-item"
                  onClick={() => {
                    close();
                    navigate(`/plans/${duplicatePlan(plan.id).id}`);
                  }}
                >
                  <Icon name="copy" /> Duplicate plan
                </button>
                <button
                  type="button"
                  className="menu-item"
                  onClick={() => {
                    close();
                    setConfirmDelete(true);
                  }}
                >
                  <Icon name="trash" /> Delete plan
                </button>
              </>
            )}
          </Popover>
        </div>
      </div>

      {/* ---------- Details ---------- */}
      <div className="inv-card plan-details">
        <div className="form-grid plan-grid">
          <div className="form-field">
            <label htmlFor="pl-client">Client</label>
            <input id="pl-client" className="form-input" value={plan.client} onChange={set("client")} placeholder="Brand or agency" />
          </div>
          <div className="form-field">
            <label htmlFor="pl-start">Start</label>
            <input id="pl-start" className="form-input" type="date" value={plan.startDate} onChange={set("startDate")} />
          </div>
          <div className="form-field">
            <label htmlFor="pl-end">End</label>
            <input id="pl-end" className="form-input" type="date" value={plan.endDate} min={plan.startDate || undefined} onChange={set("endDate")} />
          </div>
          <div className="form-field span-2">
            <span className="form-label">Total budget</span>
            {currencies.length ? (
              <div className="plan-budgets">
                {currencies.map((currency) => (
                  <label key={currency} className="plan-budget">
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step="any"
                      aria-label={`Total budget in ${currency}`}
                      value={num(budgets[currency])}
                      onChange={(e) => setBudget(currency, e.target.value)}
                      placeholder="0"
                    />
                    <span>{currency}</span>
                  </label>
                ))}
              </div>
            ) : (
              <span className="form-hint">Add channels to set a budget for each of their currencies.</span>
            )}
          </div>
          <div className="form-field span-2">
            <label htmlFor="pl-notes">Notes</label>
            <textarea
              id="pl-notes"
              className="form-input plan-notes"
              value={plan.notes}
              onChange={set("notes")}
              placeholder="Targeting, flighting, anything the client should know"
              rows={2}
            />
          </div>
        </div>
      </div>

      {/* ---------- Totals ---------- */}
      <div className="inv-stats">
        <div className="stat">
          <span>Allocated</span>
          {totals.byCurrency.length === 0 && <strong>—</strong>}
          {totals.byCurrency.map((c) => {
            const over = c.planned > 0 && c.allocated > c.planned;
            return (
              <div key={c.currency} className="plan-money">
                <strong>
                  {formatAmount(c.allocated)} <small>{c.currency}</small>
                </strong>
                {c.planned > 0 && (
                  <>
                    <div className={`plan-bar${over ? " is-over" : ""}`}>
                      <span style={{ width: `${Math.min(100, (c.allocated / c.planned) * 100)}%` }} />
                    </div>
                    <em className={`plan-left${over ? " is-over" : ""}`}>
                      {over
                        ? `${formatAmount(-c.remaining, c.currency)} over budget`
                        : `${formatAmount(c.remaining, c.currency)} left of ${formatAmount(c.planned)}`}
                    </em>
                  </>
                )}
              </div>
            );
          })}
        </div>
        <div className="stat">
          <span>Est. impressions</span>
          <strong>{formatCount(totals.impressions)}</strong>
        </div>
        <div className="stat">
          <span>Average CPM</span>
          {totals.byCurrency.every((c) => c.avgCpm == null) && <strong>—</strong>}
          {totals.byCurrency
            .filter((c) => c.avgCpm != null)
            .map((c) => (
              <strong key={c.currency} className="plan-money">
                {c.avgCpm.toFixed(2)} <small>{c.currency}</small>
              </strong>
            ))}
        </div>
        <div className="stat">
          <span>Channels</span>
          <strong>
            {totals.channels} <small>in {totals.territories} {totals.territories === 1 ? "territory" : "territories"}</small>
          </strong>
        </div>
      </div>

      {(totals.unpriced > 0 || lines.some((l) => l.missing)) && (
        <div className="plan-notices">
          {totals.unpriced > 0 && <p>Some channels have a budget but no CPM, so their impressions can't be estimated.</p>}
          {lines.some((l) => l.missing) && (
            <p>Some channels were deleted from the inventory. They're shown with the details saved when they were added.</p>
          )}
        </div>
      )}

      {/* ---------- Lines ---------- */}
      <div className="inv-card">
        <div className="inv-toolbar no-print">
          <span className="inv-count">
            {lines.length} {lines.length === 1 ? "channel" : "channels"}
          </span>
          {lines.length > 0 && (
            <div className="bulk">
              <span className="plan-split-label">Split budget</span>
              <button
                type="button"
                className="btn"
                disabled={!hasBudget}
                title={hasBudget ? "Each currency's budget, same amount on every channel" : "Set a budget first"}
                onClick={() => setItems(plan.id, splitBudget(plan, lines, "even"))}
              >
                Evenly
              </button>
              <button
                type="button"
                className="btn"
                disabled={!hasBudget}
                title={hasBudget ? "Each currency's budget, more where reach is bigger" : "Set a budget first"}
                onClick={() => setItems(plan.id, splitBudget(plan, lines, "reach"))}
              >
                By reach
              </button>
              <button
                type="button"
                className="btn-link"
                onClick={() => setItems(plan.id, plan.items.map((i) => ({ ...i, budget: 0 })))}
              >
                Clear budgets
              </button>
            </div>
          )}
        </div>

        {lines.length === 0 ? (
          <div className="inv-empty">
            <h2>No channels yet</h2>
            <p>
              In the inventory, select channels and choose <strong>Add to plan</strong>.
            </p>
            <div className="inv-empty-actions">
              <Link to="/inventory" className="btn-primary">
                <Icon name="plus" /> Add channels
              </Link>
            </div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="inv-table plan-table">
              <thead>
                <tr>
                  <th className="col-channelName">Channel</th>
                  <th>Territory</th>
                  <th>Format</th>
                  <th className="num">Reach</th>
                  <th className="num">List CPM</th>
                  <th className="num">Plan CPM</th>
                  <th className="num">Budget</th>
                  <th className="num">Share of budget</th>
                  <th className="num">Est. impressions</th>
                  <th className="col-actions no-print">
                    <span className="sr-only">Remove</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const share = lineShare(l, totals);
                  return (
                    <tr key={l.item.channelId} className={l.missing ? "is-missing" : ""}>
                      <td className="col-channelName">
                        <span className="cell-strong">{l.channel.channelName}</span>
                        <div className="plan-client">
                          {l.channel.partner}
                          {l.missing && " · deleted from inventory"}
                        </div>
                      </td>
                      <td>{joinList(l.channel.territories) || "—"}</td>
                      <td>{joinList(l.channel.formats) || "—"}</td>
                      <td className="num">{formatReach(l.channel.reach) || "—"}</td>
                      <td className="num">{l.listCpm == null ? "—" : formatMoney(l.listCpm, l.currency)}</td>
                      <td className="num">
                        <input
                          className={`plan-input${l.negotiated ? " is-set" : ""}`}
                          type="number"
                          min="0"
                          step="any"
                          aria-label={`Plan CPM for ${l.channel.channelName}`}
                          placeholder={l.listCpm == null ? "CPM" : String(l.listCpm)}
                          value={num(l.item.cpm)}
                          onChange={(e) =>
                            updateLine(plan.id, l.item.channelId, {
                              cpm: e.target.value === "" ? null : Number(e.target.value),
                            })
                          }
                        />
                      </td>
                      <td className="num">
                        <input
                          className="plan-input"
                          type="number"
                          min="0"
                          step="any"
                          aria-label={`Budget for ${l.channel.channelName}`}
                          value={l.item.budget || ""}
                          placeholder="0"
                          onChange={(e) =>
                            updateLine(plan.id, l.item.channelId, { budget: Number(e.target.value) || 0 })
                          }
                        />
                        <span className="plan-cur">{l.currency}</span>
                      </td>
                      <td className="num">{share == null ? "—" : `${(share * 100).toFixed(1)}%`}</td>
                      <td className="num">{formatCount(l.impressions)}</td>
                      <td className="col-actions no-print">
                        <div className="row-actions">
                          <button
                            type="button"
                            className="icon-btn danger"
                            title="Remove from plan"
                            aria-label={`Remove ${l.channel.channelName} from plan`}
                            onClick={() => removeLine(plan.id, l.item.channelId)}
                          >
                            <Icon name="x" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td className="col-channelName">Total</td>
                  <td colSpan={5} />
                  <td className="num">
                    {byCurrency((c) => (
                      <div key={c.currency}>{formatAmount(c.allocated, c.currency)}</div>
                    ))}
                  </td>
                  <td className="num" />
                  <td className="num">{formatCount(totals.impressions)}</td>
                  <td className="col-actions no-print" />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {confirmDelete && (
        <Modal
          title="Delete plan?"
          onClose={() => setConfirmDelete(false)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setConfirmDelete(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={() => {
                  deletePlan(plan.id);
                  navigate("/plans", { replace: true });
                }}
              >
                Delete plan
              </button>
            </>
          }
        >
          <p className="modal-text">
            <strong>{plan.name}</strong> will be removed. The channels in the inventory aren't affected.
          </p>
        </Modal>
      )}
    </div>
  );
}
